import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Sparkles,
  Scissors,
  Palette,
  Eye as EyeIcon,
  ChefHat,
  Heart,
  Plane,
  ShoppingBag,
  Leaf,
  PartyPopper,
  GraduationCap,
  Save,
  ChevronRight,
  ChevronDown,
  Settings,
  Users,
  LayoutDashboard,
  ClipboardList,
  Image,
  BarChart3,
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  Calendar,
  MapPin,
  Globe,
  Link2,
  MessageSquare,
  Music,
  Monitor,
  Zap,
  Edit3,
  Mail,
  Phone,
  User,
  Building2,
  Briefcase,
  Plus,
  Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  "party-popper": PartyPopper, "graduation-cap": GraduationCap, "sparkles": Sparkles,
  "scissors": Scissors, "palette": Palette, "eye": EyeIcon, "chef-hat": ChefHat,
  "heart": Heart, "plane": Plane, "shopping-bag": ShoppingBag, "leaf": Leaf,
};

const STATUS_COLORS = {
  live: "bg-emerald-100 text-emerald-700", pilot: "bg-blue-100 text-blue-700",
  coming_soon: "bg-amber-100 text-amber-700",
};

const ALL_DEFAULTS = [
  { key: "dietary", label: "Dietary" }, { key: "accessibility", label: "Accessibility" },
  { key: "interests", label: "Interests" }, { key: "audience", label: "Audience" },
  { key: "face", label: "Face/Skin" }, { key: "hair", label: "Hair" },
  { key: "nails", label: "Nails" }, { key: "lash", label: "Lash" },
  { key: "wellness", label: "Wellness" }, { key: "style", label: "Style" },
  { key: "travel", label: "Travel" }, { key: "culinary", label: "Culinary" },
  { key: "dispensary", label: "Dispensary" },
];

const INTAKE_TOGGLES = [
  { key: "collect_personal_info", label: "Personal Info (name, email, phone, city)", default: true },
  { key: "collect_builder_profile", label: "Builder Profile (role, project, stage)" },
  { key: "collect_audience_prefs", label: "Audience Preferences (learning style, pace, interaction)" },
  { key: "collect_accessibility", label: "Accessibility (digital, physical, invisible)" },
  { key: "collect_dietary", label: "Dietary Preferences" },
  { key: "collect_interests", label: "Interests (industries, topics)" },
  { key: "collect_questions", label: "Question Bank (ask ahead)" },
  { key: "collect_tap_in", label: "Tap In (playlist link)" },
  { key: "collect_inspo", label: "Inspiration Upload" },
  { key: "collect_face", label: "Face/Skin Profile (makeup)" },
  { key: "collect_hair_profile", label: "Hair Profile" },
  { key: "collect_nails_profile", label: "Nails Profile" },
  { key: "collect_lash_profile", label: "Lash Profile" },
  { key: "collect_wellness_profile", label: "Wellness Profile" },
  { key: "collect_culinary_profile", label: "Culinary/Dietary Detail" },
  { key: "collect_travel_profile", label: "Travel Preferences" },
  { key: "collect_style_profile", label: "Style Preferences" },
];

export default function CategoryTemplateEditor({ embedded = false, initialCategory = null }) {
  const [presets, setPresets] = useState([]);
  const [selected, setSelected] = useState(initialCategory);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => { fetchPresets(); }, []);

  // Reset selection when initialCategory prop changes (fixes admin host dashboard context switching)
  useEffect(() => {
    if (initialCategory && initialCategory !== selected && presets.length > 0) {
      const preset = presets.find((p) => p.category === initialCategory);
      if (preset) {
        setSelected(initialCategory);
        setEditing(JSON.parse(JSON.stringify(preset)));
        setActiveTab("overview");
      }
    }
  }, [initialCategory, presets]);

  const fetchPresets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/category-presets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPresets(data);
        // If initialCategory provided, select it; otherwise first preset
        const target = initialCategory || (data.length > 0 ? data[0].category : null);
        if (target) {
          const preset = data.find((p) => p.category === target);
          if (preset) {
            setSelected(target);
            setEditing(JSON.parse(JSON.stringify(preset)));
          } else if (!selected && data.length > 0) {
            setSelected(data[0].category);
            setEditing(JSON.parse(JSON.stringify(data[0])));
          }
        }
      }
    } catch (err) {
      toast.error("Failed to load category presets");
    } finally {
      setLoading(false);
    }
  };

  const selectCategory = (cat) => {
    const preset = presets.find((p) => p.category === cat);
    setSelected(cat);
    setEditing(JSON.parse(JSON.stringify(preset)));
    setActiveTab("overview");
  };

  const savePreset = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/category-presets/${editing.category}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editing),
      });
      if (res.ok) {
        toast.success(`${editing.display_name} template saved`);
        fetchPresets();
      } else {
        toast.error("Failed to save");
      }
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setEditing((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const toggleDefault = (key) => {
    const current = editing?.defaults_to_collect || [];
    if (current.includes(key)) {
      updateField("defaults_to_collect", current.filter((d) => d !== key));
    } else {
      updateField("defaults_to_collect", [...current, key]);
    }
  };

  // Map intake toggle keys to defaults_to_collect keys
  const TOGGLE_TO_DEFAULT_MAP = {
    collect_builder_profile: "builder",
    collect_audience_prefs: "audience",
    collect_accessibility: "accessibility",
    collect_interests: "interests",
    collect_dietary: "dietary",
    collect_tap_in: "tap_in",
    collect_questions: "questions",
    collect_inspo: "inspo",
    collect_face: "face",
    collect_hair_profile: "hair",
    collect_nails_profile: "nails",
    collect_lash_profile: "lash",
    collect_wellness_profile: "wellness",
    collect_culinary_profile: "culinary",
    collect_travel_profile: "travel",
    collect_style_profile: "style",
  };

  const isIntakeEnabled = (key) => {
    // First check intake_settings for explicit boolean
    const intake = editing?.intake_settings || {};
    if (intake[key] !== undefined) return !!intake[key];
    // Then check defaults_to_collect
    const defaultKey = TOGGLE_TO_DEFAULT_MAP[key];
    if (defaultKey && editing?.defaults_to_collect) {
      return editing.defaults_to_collect.includes(defaultKey);
    }
    // Fall back to INTAKE_TOGGLES default
    const toggle = INTAKE_TOGGLES.find(t => t.key === key);
    return !!(toggle?.default);
  };

  const toggleIntake = (key) => {
    const currentEnabled = isIntakeEnabled(key);
    // Update intake_settings boolean
    updateField(`intake_settings.${key}`, !currentEnabled);
    // Also update defaults_to_collect if there's a mapping
    const defaultKey = TOGGLE_TO_DEFAULT_MAP[key];
    if (defaultKey) {
      const current = editing?.defaults_to_collect || [];
      if (!currentEnabled) {
        // Turning ON  - add to defaults_to_collect
        if (!current.includes(defaultKey)) {
          updateField("defaults_to_collect", [...current, defaultKey]);
        }
      } else {
        // Turning OFF  - remove from defaults_to_collect
        updateField("defaults_to_collect", current.filter(d => d !== defaultKey));
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const IconComp = selected ? (ICON_MAP[editing?.icon] || Sparkles) : Sparkles;

  return (
    <div className={embedded ? "" : "p-8"} data-testid="category-template-editor">
      {!(embedded && initialCategory) && (
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Category Host Dashboard Templates</h2>
          <p className="text-sm text-gray-500">
            Configure the default host dashboard for each category. New events inherit these settings.
          </p>
        </div>
        {editing && (
          <Button onClick={savePreset} disabled={saving} className="gap-2" data-testid="save-template-btn">
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Template"}
          </Button>
        )}
      </div>
      )}

      <div className="flex gap-6">
        {/* Category Sidebar - hide when embedded with a specific category */}
        {!(embedded && initialCategory) && (
        <div className="w-56 flex-shrink-0">
          <div className="space-y-1" data-testid="category-list">
            {presets.map((p) => {
              const Icon = ICON_MAP[p.icon] || Sparkles;
              const isActive = selected === p.category;
              return (
                <button
                  key={p.category}
                  onClick={() => selectCategory(p.category)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all text-sm ${
                    isActive ? "bg-gray-900 text-white shadow-md" : "hover:bg-gray-100 text-gray-700"
                  }`}
                  data-testid={`category-btn-${p.category}`}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: isActive ? "rgba(255,255,255,0.2)" : p.color + "15", color: isActive ? "white" : p.color }}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.display_name}</p>
                  </div>
                  <Badge className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[p.status] || "bg-gray-100"}`}>
                    {p.status === "coming_soon" ? "Soon" : p.status}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Main Editor */}
        {editing && (
          <div className="flex-1 min-w-0">
            {/* Category Header - feels like a real dashboard */}
            <div
              className="rounded-xl p-6 mb-6 text-white relative overflow-hidden"
              style={{ backgroundColor: editing.color }}
              data-testid="template-header"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <IconComp className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{editing.display_name} Dashboard</h3>
                    <p className="text-white/70 text-sm">
                      Default template for all {editing.display_name.toLowerCase()} events
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`${STATUS_COLORS[editing.status]}`}>
                    {editing.status === "coming_soon" ? "Coming Soon" : editing.status === "pilot" ? "Pilot" : "Live"}
                  </Badge>
                  <Badge className="bg-white/20 text-white border-0">
                    {editing.stats?.events || 0} events
                  </Badge>
                  <Badge className="bg-white/20 text-white border-0">
                    {editing.stats?.rsvps || 0} RSVPs
                  </Badge>
                </div>
              </div>
            </div>

            {/* Dashboard Tabs - mirrors real host dashboard */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="overview" className="gap-1.5">
                  <LayoutDashboard className="w-3.5 h-3.5" />Overview
                </TabsTrigger>
                <TabsTrigger value="welcome" className="gap-1.5">
                  <Globe className="w-3.5 h-3.5" />Welcome Page
                </TabsTrigger>
                <TabsTrigger value="intake" className="gap-1.5">
                  <ClipboardList className="w-3.5 h-3.5" />Intake Steps
                </TabsTrigger>
                <TabsTrigger value="confirmation" className="gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />Confirmation
                </TabsTrigger>
                <TabsTrigger value="insights" className="gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5" />Insights
                </TabsTrigger>
                <TabsTrigger value="branding" className="gap-1.5">
                  <Palette className="w-3.5 h-3.5" />Branding
                </TabsTrigger>
                <TabsTrigger value="share-growth" className="gap-1.5" data-testid="category-tab-share-growth">
                  <Link2 className="w-3.5 h-3.5" />Share &amp; Growth
                </TabsTrigger>
              </TabsList>

              {/* OVERVIEW TAB */}
              <TabsContent value="overview">
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <StatCard label="Status" value={editing.status === "coming_soon" ? "Coming Soon" : editing.status} icon={Zap} color={editing.color} />
                  <StatCard label="Events" value={editing.stats?.events || 0} icon={Calendar} color={editing.color} />
                  <StatCard label="Total RSVPs" value={editing.stats?.rsvps || 0} icon={Users} color={editing.color} />
                </div>

                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base">Category Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Display Name</Label>
                        <Input value={editing.display_name} onChange={(e) => updateField("display_name", e.target.value)} data-testid="display-name-input" />
                      </div>
                      <div>
                        <Label>Card Label</Label>
                        <Input value={editing.card_label || ""} onChange={(e) => updateField("card_label", e.target.value)} placeholder="e.g. PARTICIPANT CARD" data-testid="card-label-input" />
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Select value={editing.status} onValueChange={(v) => updateField("status", v)}>
                          <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="live">Live</SelectItem>
                            <SelectItem value="pilot">Pilot</SelectItem>
                            <SelectItem value="coming_soon">Coming Soon</SelectItem>
                            <SelectItem value="disabled">Disabled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Color</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={editing.color} onChange={(e) => updateField("color", e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                          <Input value={editing.color} onChange={(e) => updateField("color", e.target.value)} className="flex-1 font-mono text-sm" data-testid="color-input" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Feature Toggles */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base">Feature Toggles</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Personal Inspo Folder</p>
                        <p className="text-xs text-gray-500">Users can save inspo for this category</p>
                      </div>
                      <Switch
                        checked={editing.inspo_enabled !== false}
                        onCheckedChange={(v) => updateField("inspo_enabled", v)}
                        data-testid="toggle-inspo"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Host Inspo Gallery</p>
                        <p className="text-xs text-gray-500">Hosts can curate inspo for their clients</p>
                      </div>
                      <Switch
                        checked={editing.host_inspo_enabled === true}
                        onCheckedChange={(v) => updateField("host_inspo_enabled", v)}
                        data-testid="toggle-host-inspo"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Tap In (Music)</p>
                        <p className="text-xs text-gray-500">Enable playlist sharing in RSVP flow</p>
                      </div>
                      <Switch
                        checked={editing.tap_in_enabled === true}
                        onCheckedChange={(v) => updateField("tap_in_enabled", v)}
                        data-testid="toggle-tap-in"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">/me Access</p>
                        <p className="text-xs text-gray-500">Users with this category can access personal profile</p>
                      </div>
                      <Switch
                        checked={editing.me_access_enabled === true}
                        onCheckedChange={(v) => updateField("me_access_enabled", v)}
                        data-testid="toggle-me-access"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Sales Page</p>
                        <p className="text-xs text-gray-500">Public category sales page is accessible</p>
                      </div>
                      <Switch
                        checked={editing.sales_page_enabled !== false}
                        onCheckedChange={(v) => updateField("sales_page_enabled", v)}
                        data-testid="toggle-sales-page"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Default Blocks Matrix */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base">Default Blocks Matrix</CardTitle>
                    <p className="text-xs text-gray-500">ON = always collected, Optional = user can skip, OFF = hidden</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {ALL_DEFAULTS.map((d) => {
                        const blocks = editing.default_blocks || {};
                        const state = blocks[d.key] || "off";
                        return (
                          <button
                            key={d.key}
                            onClick={() => {
                              const next = state === "off" ? "on" : state === "on" ? "optional" : "off";
                              updateField("default_blocks", { ...blocks, [d.key]: next });
                            }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                              state === "on" ? "border-green-500 bg-green-50 text-green-700" :
                              state === "optional" ? "border-amber-400 bg-amber-50 text-amber-700" :
                              "border-gray-200 text-gray-400 hover:border-gray-300"
                            }`}
                            data-testid={`block-toggle-${d.key}`}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center text-[9px] font-bold ${
                              state === "on" ? "bg-green-500 border-green-500 text-white" :
                              state === "optional" ? "bg-amber-400 border-amber-400 text-white" :
                              "border-gray-300"
                            }`}>
                              {state === "on" ? "ON" : state === "optional" ? "O" : ""}
                            </div>
                            <span className="truncate">{d.label}</span>
                            <span className="ml-auto text-[9px] uppercase opacity-60">{state}</span>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Linked Pilots */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base">Linked Pilots</CardTitle>
                    <p className="text-xs text-gray-500">Organizations actively running this category (queried from real events)</p>
                  </CardHeader>
                  <CardContent>
                    <LinkedPilotsReal category={editing.category} API={API} />
                  </CardContent>
                </Card>

                {/* Pipeline Stages */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Pipeline Stages</CardTitle>
                    <p className="text-xs text-gray-500">Host Kanban board stages for client management</p>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {(editing.pipeline_stages || ["Invite", "Shared Preferences", "Confirm", "Completed", "Follow Up", "Archive"]).map((stage, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs py-1 px-3">{stage}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* WELCOME PAGE TAB */}
              <TabsContent value="welcome">
                <WelcomePageEditor editing={editing} updateField={updateField} />
              </TabsContent>

              {/* INTAKE STEPS TAB */}
              <TabsContent value="intake">
                <IntakeStepsEditor editing={editing} updateField={updateField} toggleIntake={toggleIntake} isIntakeEnabled={isIntakeEnabled} />
              </TabsContent>

              {/* CONFIRMATION TAB */}
              <TabsContent value="confirmation">
                <ConfirmationEditor editing={editing} updateField={updateField} />
              </TabsContent>

              {/* INSIGHTS TAB */}
              <TabsContent value="insights">
                <InsightsPreview editing={editing} />
              </TabsContent>

              {/* BRANDING TAB */}
              <TabsContent value="branding">
                <BrandingEditor editing={editing} updateField={updateField} />
              </TabsContent>

              {/* SHARE & GROWTH TAB - Iter 9.40 Pass I */}
              <TabsContent value="share-growth">
                <ShareGrowthEditor editing={editing} updateField={updateField} />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}

// Linked Pilots - full CRUD with search, add, remove
function LinkedPilotsReal({ category, API }) {
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);

  const fetchPilots = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/category-presets/${category}/linked-pilots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPilots(data.pilots || []);
      }
    } catch (err) { console.error("Failed to fetch linked pilots:", err); }
    setLoading(false);
  };

  useEffect(() => {
    if (category) fetchPilots();
  }, [category, API]);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/users/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.users || data || []);
      }
    } catch (err) { console.error(err); }
    setSearching(false);
  };

  const handleAddPilot = async (user) => {
    setAdding(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/category-presets/${category}/linked-pilots`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, org_id: user.org_id || '' })
      });
      if (res.ok) {
        toast.success(`Added ${user.first_name || user.email} to ${category} pilot`);
        setShowAddDialog(false);
        setSearchQuery('');
        setSearchResults([]);
        fetchPilots();
      }
    } catch (err) { toast.error('Failed to add pilot'); }
    setAdding(false);
  };

  const handleRemovePilot = async (pilot) => {
    if (!window.confirm(`Remove ${pilot.org_name || pilot.owner_name || 'this pilot'} from ${category}?`)) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/category-presets/${category}/linked-pilots`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: pilot.user_id || '', org_id: pilot.org_id || '' })
      });
      if (res.ok) {
        toast.success('Pilot removed');
        fetchPilots();
      }
    } catch (err) { toast.error('Failed to remove pilot'); }
  };

  if (loading) return <div className="text-center py-4"><RefreshCw className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>;

  return (
    <div className="space-y-3" data-testid="linked-pilots-list">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{pilots.length} linked pilot{pilots.length !== 1 ? 's' : ''}</p>
        <Button size="sm" variant="outline" onClick={() => setShowAddDialog(true)} className="gap-1 text-xs" data-testid="add-pilot-btn">
          <Plus className="w-3 h-3" /> Add User/Org
        </Button>
      </div>

      {pilots.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">No linked pilots for this category. Click "Add User/Org" to link one.</p>
      ) : (
        pilots.map((pilot, idx) => (
          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden">
                {pilot.org_logo ? (
                  <img src={pilot.org_logo} alt={pilot.org_name} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-4 h-4 text-gray-400" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium">{pilot.org_name || pilot.owner_name || 'Unknown'}</p>
                <p className="text-xs text-gray-400">{pilot.owner_email}</p>
                {pilot.sample_event && <p className="text-xs text-gray-400">Event: {pilot.sample_event}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {pilot.event_count > 0 && (
                <Badge variant="outline" className="text-[10px]">{pilot.event_count} event{pilot.event_count !== 1 ? 's' : ''}</Badge>
              )}
              <Badge className={`text-[10px] ${pilot.source === 'manual' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                {pilot.source === 'manual' ? 'Manual' : 'Active'}
              </Badge>
              <Button size="sm" variant="ghost" onClick={() => handleRemovePilot(pilot)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700" data-testid={`remove-pilot-${idx}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))
      )}

      {/* Add Pilot Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddDialog(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Add User/Org to {category} Pilot</h3>
            <Input
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              data-testid="pilot-search-input"
              autoFocus
            />
            <div className="mt-3 max-h-60 overflow-y-auto space-y-2">
              {searching && <p className="text-xs text-gray-400 text-center py-2">Searching...</p>}
              {searchResults.map((user, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer" onClick={() => handleAddPilot(user)}>
                  <div>
                    <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
                    <p className="text-xs text-gray-500">{user.email}{user.company ? ` - ${user.company}` : ''}</p>
                  </div>
                  <Button size="sm" variant="ghost" disabled={adding} className="text-xs gap-1">
                    <Plus className="w-3 h-3" /> Add
                  </Button>
                </div>
              ))}
              {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-2">No users found</p>
              )}
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "15", color }}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-lg font-bold capitalize">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function WelcomePageEditor({ editing, updateField }) {
  const welcome = editing.welcome || {};
  return (
    <div className="space-y-6">
      {/* Live Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Monitor className="w-4 h-4" /> Welcome Page Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl overflow-hidden border max-w-sm mx-auto" style={{ backgroundColor: editing.color }}>
            <div className="p-6 text-white text-center">
              <div className="w-20 h-20 rounded-full bg-white/20 mx-auto mb-4 flex items-center justify-center">
                {(() => { const I = ICON_MAP[editing.icon] || Sparkles; return <I className="w-10 h-10" />; })()}
              </div>
              <h3 className="text-xl font-bold">{welcome.title_template || editing.display_name + " Experience"}</h3>
              {welcome.subtitle_template && <p className="text-white/70 text-sm mt-1">{welcome.subtitle_template}</p>}
              <p className="text-white/60 text-xs mt-3">{welcome.description_template || "Join us for an amazing experience."}</p>
              <div className="mt-4 flex items-center justify-center gap-2 text-white/50 text-xs">
                <Calendar className="w-3 h-3" />
                <span>Date/Time TBD</span>
                <span className="mx-1">-</span>
                <MapPin className="w-3 h-3" />
                <span>Location TBD</span>
              </div>
              {welcome.show_hosted_by !== false && (
                <div className="mt-4 pt-3 border-t border-white/20">
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">Hosted By</p>
                  <p className="text-sm text-white/80">Host Name</p>
                  {welcome.show_social_links !== false && (
                    <div className="flex justify-center gap-3 mt-2">
                      <Globe className="w-3.5 h-3.5 text-white/40" />
                      <Link2 className="w-3.5 h-3.5 text-white/40" />
                      <Mail className="w-3.5 h-3.5 text-white/40" />
                    </div>
                  )}
                </div>
              )}
              <Button className="mt-4 bg-white text-gray-900 hover:bg-white/90 text-sm w-full">Begin</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Editable Fields */}
      <Card>
        <CardHeader><CardTitle className="text-base">Welcome Page Defaults</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Default Title Template</Label>
            <Input value={welcome.title_template || ""} onChange={(e) => updateField("welcome.title_template", e.target.value)} placeholder="e.g. Build Once, Edit Forever" data-testid="welcome-title-input" />
          </div>
          <div>
            <Label>Default Subtitle Template</Label>
            <Input value={welcome.subtitle_template || ""} onChange={(e) => updateField("welcome.subtitle_template", e.target.value)} placeholder="Optional subtitle" data-testid="welcome-subtitle-input" />
          </div>
          <div>
            <Label>Default Description</Label>
            <Textarea value={welcome.description_template || ""} onChange={(e) => updateField("welcome.description_template", e.target.value)} placeholder="Event description template" rows={3} data-testid="welcome-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <Label className="text-sm">Show Hosted By</Label>
              <Switch checked={welcome.show_hosted_by !== false} onCheckedChange={(v) => updateField("welcome.show_hosted_by", v)} data-testid="welcome-hosted-by-toggle" />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <Label className="text-sm">Show Social Links</Label>
              <Switch checked={welcome.show_social_links !== false} onCheckedChange={(v) => updateField("welcome.show_social_links", v)} data-testid="welcome-socials-toggle" />
            </div>
            {editing.category === "instructor" && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <Label className="text-sm">Show Instructor Section</Label>
                <Switch checked={welcome.show_instructor_section !== false} onCheckedChange={(v) => updateField("welcome.show_instructor_section", v)} data-testid="welcome-instructor-toggle" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function IntakeStepsEditor({ editing, updateField, toggleIntake, isIntakeEnabled }) {
  const steps = editing.rsvp_steps || [];
  const intake = editing.intake_settings || {};
  const [expandedIntake, setExpandedIntake] = useState(null);
  const [newQuestion, setNewQuestion] = useState("");

  // Map intake toggle keys to rsvp_steps by matching field names
  const TOGGLE_STEP_MAP = {
    collect_personal_info: ['first_name', 'last_name', 'email', 'phone', 'city', 'country'],
    collect_builder_profile: ['company', 'title', 'role', 'bio', 'what_building', 'stage', 'project_link', 'socials', 'photo'],
    collect_audience_prefs: ['experience_level', 'learning_style', 'pace', 'interaction_style'],
    collect_accessibility: ['accessibility', 'digital_accessibility', 'physical_accessibility'],
    collect_dietary: ['dietary', 'dietary_restrictions'],
    collect_interests: ['interests', 'topics', 'industries'],
    collect_questions: ['questions'],
    collect_tap_in: ['playlist_link', 'music'],
    collect_inspo: ['inspo', 'inspiration'],
    collect_face: ['skin_type', 'skin_tone', 'undertone', 'skin_concerns', 'foundation_shade'],
    collect_hair_profile: ['hair_type', 'hair_texture', 'hair_length', 'hair_color', 'hair_concerns'],
    collect_nails_profile: ['nail_shape', 'nail_length', 'nail_concerns'],
    collect_lash_profile: ['lash_type', 'lash_length', 'lash_curl'],
    collect_wellness_profile: ['wellness_goals', 'fitness_level', 'stress_level'],
    collect_culinary_profile: ['cuisine_preference', 'allergies', 'spice_level', 'cooking_level'],
    collect_travel_profile: ['travel_style', 'budget_range', 'accommodation_preference'],
    collect_style_profile: ['style_preference', 'size', 'colors', 'brands'],
  };

  const getStepFieldsForToggle = (toggleKey, stepsArr) => {
    const relevantFields = TOGGLE_STEP_MAP[toggleKey] || [];
    if (relevantFields.length === 0) return [];
    const found = [];
    for (const step of stepsArr) {
      for (const field of (step.fields || [])) {
        const fieldName = typeof field === 'string' ? field : (field.name || field.key || '');
        if (relevantFields.some(rf => fieldName.toLowerCase().includes(rf))) {
          found.push(field);
        }
      }
    }
    return found;
  };

  // Manage specific questions within each intake toggle
  const addIntakeQuestion = (toggleKey) => {
    if (!newQuestion.trim()) return;
    const questionsKey = `${toggleKey}_questions`;
    const existing = intake[questionsKey] || [];
    updateField(`intake_settings.${questionsKey}`, [...existing, { 
      id: `q_${Date.now()}`, 
      text: newQuestion.trim(), 
      type: "text",
      required: false 
    }]);
    setNewQuestion("");
  };

  const removeIntakeQuestion = (toggleKey, questionId) => {
    const questionsKey = `${toggleKey}_questions`;
    const existing = intake[questionsKey] || [];
    updateField(`intake_settings.${questionsKey}`, existing.filter(q => q.id !== questionId));
  };

  const updateIntakeQuestion = (toggleKey, questionId, field, value) => {
    const questionsKey = `${toggleKey}_questions`;
    const existing = intake[questionsKey] || [];
    updateField(`intake_settings.${questionsKey}`, existing.map(q => 
      q.id === questionId ? { ...q, [field]: value } : q
    ));
  };

  return (
    <div className="space-y-6">
      {/* RSVP Steps Visual Flow */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> RSVP Flow Steps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            {steps.map((step, idx) => (
              <div key={idx} className="flex items-center gap-2 flex-shrink-0">
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 min-w-[160px]" style={{ borderColor: editing.color + "40", backgroundColor: editing.color + "08" }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: editing.color }}>
                    {idx}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{step.name}</p>
                    <p className="text-[10px] text-gray-400">{step.fields?.length || 0} fields</p>
                  </div>
                </div>
                {idx < steps.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />}
              </div>
            ))}
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-emerald-200 bg-emerald-50 flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <p className="text-sm font-medium text-emerald-700">Confirmation</p>
            </div>
          </div>

          {/* Step Detail Editors */}
          {steps.map((step, idx) => (
            <StepEditor key={idx} step={step} index={idx} editing={editing} updateField={updateField} />
          ))}
        </CardContent>
      </Card>

      {/* Intake Collection Settings with CRUD */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Intake Collection Settings</CardTitle>
          <p className="text-xs text-gray-500">
            Toggle intake categories on/off. Expand each to manage specific questions. Hosts can override toggles but cannot add categories outside this template.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {INTAKE_TOGGLES.map((toggle) => {
              const isEnabled = isIntakeEnabled(toggle.key);
              const isExpanded = expandedIntake === toggle.key;
              const questionsKey = `${toggle.key}_questions`;
              const questions = intake[questionsKey] || [];
              
              return (
                <div key={toggle.key} className="border rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between py-2.5 px-3 hover:bg-gray-50 transition-colors">
                    <button 
                      className="flex items-center gap-2 flex-1 text-left"
                      onClick={() => setExpandedIntake(isExpanded ? null : toggle.key)}
                    >
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                      <span className="text-sm text-gray-700">{toggle.label}</span>
                      {questions.length > 0 && (
                        <Badge variant="outline" className="text-[10px] ml-1">{questions.length} questions</Badge>
                      )}
                    </button>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => toggleIntake(toggle.key)}
                      data-testid={`intake-toggle-${toggle.key}`}
                    />
                  </div>
                  
                  {/* Expanded CRUD for specific questions */}
                  {isExpanded && (
                    <div className="px-4 pb-3 pt-1 border-t bg-gray-50/50">
                      {/* Show existing rsvp_steps fields that map to this toggle */}
                      {(() => {
                        const mappedFields = getStepFieldsForToggle(toggle.key, steps);
                        if (mappedFields.length > 0) return (
                          <div className="mb-3">
                            <p className="text-[11px] text-blue-500 font-medium mb-1.5">Template Fields (from RSVP steps)</p>
                            <div className="flex flex-wrap gap-1.5">
                              {mappedFields.map((f, i) => (
                                <span key={i} className="px-2 py-1 bg-blue-50 border border-blue-200 rounded text-[10px] text-blue-700">
                                  {typeof f === 'string' ? f.replace(/_/g, ' ') : (f.label || f.name || '').replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                        return null;
                      })()}
                      <p className="text-[11px] text-gray-400 mb-2">Custom questions (hosts can toggle on/off but cannot add new types)</p>
                      
                      {questions.length > 0 && (
                        <div className="space-y-1.5 mb-3">
                          {questions.map((q) => (
                            <div key={q.id} className="flex items-center gap-2 p-2 bg-white rounded-lg border group">
                              <Input
                                value={q.text}
                                onChange={(e) => updateIntakeQuestion(toggle.key, q.id, 'text', e.target.value)}
                                className="flex-1 h-8 text-xs border-0 bg-transparent p-0 focus-visible:ring-0"
                                data-testid={`intake-question-${q.id}`}
                              />
                              <select
                                value={q.type || 'text'}
                                onChange={(e) => updateIntakeQuestion(toggle.key, q.id, 'type', e.target.value)}
                                className="text-[10px] border rounded px-1.5 py-1 bg-white"
                              >
                                <option value="text">Text</option>
                                <option value="select">Select</option>
                                <option value="multi_select">Multi-Select</option>
                                <option value="toggle">Toggle</option>
                                <option value="scale">Scale</option>
                              </select>
                              <button
                                onClick={() => updateIntakeQuestion(toggle.key, q.id, 'required', !q.required)}
                                className={`text-[10px] px-1.5 py-0.5 rounded ${q.required ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}
                              >
                                {q.required ? 'Req' : 'Opt'}
                              </button>
                              <button
                                onClick={() => removeIntakeQuestion(toggle.key, q.id)}
                                className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                data-testid={`remove-question-${q.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add new question */}
                      <div className="flex items-center gap-2">
                        <Input
                          value={newQuestion}
                          onChange={(e) => setNewQuestion(e.target.value)}
                          placeholder="Add a question..."
                          className="flex-1 h-8 text-xs"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addIntakeQuestion(toggle.key);
                            }
                          }}
                          data-testid={`add-question-input-${toggle.key}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs gap-1"
                          onClick={() => addIntakeQuestion(toggle.key)}
                          data-testid={`add-question-btn-${toggle.key}`}
                        >
                          <Plus className="w-3 h-3" />
                          Add
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Question Bank Settings */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" />Question Bank Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Max Questions Per Attendee</Label>
              <Input type="number" min={0} max={10} value={intake.max_questions ?? 3} onChange={(e) => updateField("intake_settings.max_questions", parseInt(e.target.value) || 3)} data-testid="max-questions-input" />
            </div>
            <div>
              <Label>Question Recipients</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {["instructor", "platform", "both"].map((r) => (
                  <Badge key={r} className="text-xs capitalize cursor-default" variant="outline">{r}</Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StepEditor({ step, index, editing, updateField }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border rounded-lg mb-3">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: editing.color }}>
            {index}
          </div>
          <div className="text-left">
            <p className="text-sm font-medium">{step.name}</p>
            <p className="text-xs text-gray-400">{(step.fields || []).join(", ")}</p>
          </div>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t space-y-3">
          <div>
            <Label className="text-xs">Step Name</Label>
            <Input value={step.name} onChange={(e) => {
              const newSteps = [...(editing.rsvp_steps || [])];
              newSteps[index] = { ...newSteps[index], name: e.target.value };
              updateField("rsvp_steps", newSteps);
            }} className="text-sm" />
          </div>
          <div>
            <Label className="text-xs">Fields</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(step.fields || []).map((f, fi) => (
                <Badge key={fi} variant="outline" className="text-xs">{f}</Badge>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmationEditor({ editing, updateField }) {
  const conf = editing.confirmation || {};
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Confirmation Page Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { key: "show_event_pass", label: "Show Event Pass (title, date, location)" },
              { key: "show_instructor_section", label: "Show Instructor Section (name, headshot, LinkedIn)", show: editing.category === "instructor" },
              { key: "show_hosted_by", label: "Show Hosted By section" },
              { key: "show_preferences", label: "Show Your Preferences card" },
              { key: "show_resources", label: "Show Resources section" },
              { key: "show_check_in", label: "Show Check-in button" },
              { key: "show_tap_in", label: "Show Tap In (playlist)" },
              { key: "show_cta_links", label: "Show Custom CTA Links" },
              { key: "resources_locked_until_checkin", label: "Lock resources until check-in" },
            ].filter((item) => item.show !== false).map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50">
                <span className="text-sm">{item.label}</span>
                <Switch
                  checked={conf[item.key] !== false}
                  onCheckedChange={(v) => updateField(`confirmation.${item.key}`, v)}
                  data-testid={`conf-toggle-${item.key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Preview */}
      <Card>
        <CardHeader><CardTitle className="text-base">Confirmation Preview</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-xl border max-w-sm mx-auto overflow-hidden">
            <div className="p-4 text-center" style={{ backgroundColor: editing.color + "10" }}>
              <Badge className="text-white text-xs mb-2" style={{ backgroundColor: editing.color }}>{editing.card_label || "PREFERENCE CARD"}</Badge>
              <h4 className="font-bold text-base">Event Title Here</h4>
              <p className="text-xs text-gray-500 mt-1">Date - Time - Location</p>
            </div>
            <div className="p-4 space-y-3">
              {conf.show_preferences !== false && (
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-xs text-gray-400 uppercase">Your Preferences</p>
                  <p className="text-sm text-gray-600 mt-1">View Full Card - Edit Preferences</p>
                </div>
              )}
              {conf.show_check_in !== false && (
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 text-xs" style={{ backgroundColor: editing.color }}>Check In</Button>
                  <Button size="sm" variant="outline" className="flex-1 text-xs">Can't Make It</Button>
                </div>
              )}
              {conf.show_resources !== false && (
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-xs text-gray-400">Resources {conf.resources_locked_until_checkin !== false ? "(Locked)" : "(Unlocked)"}</p>
                </div>
              )}
              {conf.show_tap_in !== false && (
                <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-center gap-2">
                  <Music className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-400">Tap In Playlist</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function InsightsPreview({ editing }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Default Insights Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            These insights auto-generate from RSVP data for this category. Hosts see this in their dashboard.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {(editing.defaults_to_collect || []).includes("dietary") && (
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="text-xs font-medium text-orange-600 uppercase mb-2">Dietary Breakdown</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs"><span>Vegetarian</span><span className="font-medium">0</span></div>
                  <div className="flex justify-between text-xs"><span>Vegan</span><span className="font-medium">0</span></div>
                  <div className="flex justify-between text-xs"><span>Gluten-free</span><span className="font-medium">0</span></div>
                </div>
              </div>
            )}
            {(editing.defaults_to_collect || []).includes("accessibility") && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-xs font-medium text-blue-600 uppercase mb-2">Accessibility Summary</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs"><span>Digital needs</span><span className="font-medium">0</span></div>
                  <div className="flex justify-between text-xs"><span>Physical needs</span><span className="font-medium">0</span></div>
                </div>
              </div>
            )}
            {(editing.defaults_to_collect || []).includes("audience") && (
              <div className="p-4 bg-indigo-50 rounded-lg">
                <p className="text-xs font-medium text-indigo-600 uppercase mb-2">Audience Preferences</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs"><span>Beginner</span><span className="font-medium">0</span></div>
                  <div className="flex justify-between text-xs"><span>Intermediate</span><span className="font-medium">0</span></div>
                  <div className="flex justify-between text-xs"><span>Advanced</span><span className="font-medium">0</span></div>
                </div>
              </div>
            )}
            {(editing.defaults_to_collect || []).includes("interests") && (
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-xs font-medium text-purple-600 uppercase mb-2">Interest Cloud</p>
                <div className="flex flex-wrap gap-1">
                  {["Product Dev", "AI", "Design", "Growth"].map((t) => (
                    <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {editing.category === "instructor" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Instructor-Specific Insights</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-amber-50 rounded-lg">
                <p className="text-xs font-medium text-amber-600 uppercase mb-2">Question Bank</p>
                <p className="text-sm text-gray-600">Questions submitted: 0</p>
                <p className="text-sm text-gray-600">Curated for instructor: 0</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-xs font-medium text-green-600 uppercase mb-2">Topics Demand</p>
                <p className="text-sm text-gray-600">Top topic: N/A</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function BrandingEditor({ editing, updateField }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Category Branding</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={editing.color} onChange={(e) => updateField("color", e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                <Input value={editing.color} onChange={(e) => updateField("color", e.target.value)} className="font-mono text-sm" />
              </div>
            </div>
            <div>
              <Label>Icon</Label>
              <Select value={editing.icon} onValueChange={(v) => updateField("icon", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ICON_MAP).map(([key, Icon]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2"><Icon className="w-4 h-4" />{key}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Card Label (shown on preference cards)</Label>
            <Input value={editing.card_label || ""} onChange={(e) => updateField("card_label", e.target.value)} placeholder="PARTICIPANT CARD" />
          </div>

          <div>
            <Label>Inspo Folders</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {(editing.inspo_folders || []).map((f, i) => (
                <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Preview */}
      <Card>
        <CardHeader><CardTitle className="text-base">Color Preview</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3">
            <div className="text-center">
              <div className="w-full h-16 rounded-lg" style={{ backgroundColor: editing.color }} />
              <p className="text-xs text-gray-500 mt-1">Primary</p>
            </div>
            <div className="text-center">
              <div className="w-full h-16 rounded-lg" style={{ backgroundColor: editing.color + "20" }} />
              <p className="text-xs text-gray-500 mt-1">Light</p>
            </div>
            <div className="text-center">
              <div className="w-full h-16 rounded-lg" style={{ backgroundColor: editing.color + "60" }} />
              <p className="text-xs text-gray-500 mt-1">Medium</p>
            </div>
            <div className="text-center">
              <div className="w-full h-16 rounded-lg bg-gray-900" />
              <p className="text-xs text-gray-500 mt-1">Dark Text</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter 9.40 Pass I - ShareGrowthEditor
// Admin-edits `db.categories.card_viewer_labels` (hero + subtitle) +
// `db.categories.share_cta_copy` (6 fields for consumer + host growth CTAs).
// Saves via the existing PUT /api/admin/categories/{id} update path.
// ---------------------------------------------------------------------------
function ShareGrowthEditor({ editing, updateField }) {
  const labels = editing.card_viewer_labels || { hero: "", subtitle: "" };
  const cta = editing.share_cta_copy || {
    consumer_headline: "", consumer_subtext: "", consumer_cta_label: "",
    host_headline: "", host_subtext: "", host_cta_label: "",
  };
  const setLabel = (k, v) => updateField("card_viewer_labels", { ...labels, [k]: v });
  const setCta = (k, v) => updateField("share_cta_copy", { ...cta, [k]: v });

  return (
    <div className="space-y-6" data-testid="share-growth-editor">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <EyeIcon className="w-4 h-4" /> Attendee Card - Per-Category Copy
          </CardTitle>
          <p className="text-xs text-gray-500">Shown in the purple hero of the AttendeeCardModal and on `/preference-share/:cardId`.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Hero label (UPPERCASE)</Label>
            <Input
              value={labels.hero || ""}
              onChange={(e) => setLabel("hero", e.target.value)}
              placeholder={`e.g. ${(editing.name || "CATEGORY").toUpperCase()} PREFERENCE CARD`}
              data-testid="share-growth-hero"
            />
          </div>
          <div>
            <Label className="text-xs">Subtitle ("What your … sees")</Label>
            <Input
              value={labels.subtitle || ""}
              onChange={(e) => setLabel("subtitle", e.target.value)}
              placeholder="What your makeup artist sees"
              data-testid="share-growth-subtitle"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Heart className="w-4 h-4 text-purple-500" /> Consumer CTA (left card)
          </CardTitle>
          <p className="text-xs text-gray-500">Shown on the share page to anonymous visitors and clients.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Headline</Label>
            <Input value={cta.consumer_headline || ""} onChange={(e) => setCta("consumer_headline", e.target.value)} data-testid="share-growth-consumer-headline" />
          </div>
          <div>
            <Label className="text-xs">Subtext</Label>
            <Textarea rows={2} value={cta.consumer_subtext || ""} onChange={(e) => setCta("consumer_subtext", e.target.value)} data-testid="share-growth-consumer-subtext" />
          </div>
          <div>
            <Label className="text-xs">CTA label</Label>
            <Input value={cta.consumer_cta_label || ""} onChange={(e) => setCta("consumer_cta_label", e.target.value)} placeholder="Plan a session →" data-testid="share-growth-consumer-label" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-gray-700" /> Host CTA (right card)
          </CardTitle>
          <p className="text-xs text-gray-500">Shown on the share page when visitor is NOT an authed host in matching category.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Headline</Label>
            <Input value={cta.host_headline || ""} onChange={(e) => setCta("host_headline", e.target.value)} data-testid="share-growth-host-headline" />
          </div>
          <div>
            <Label className="text-xs">Subtext</Label>
            <Textarea rows={2} value={cta.host_subtext || ""} onChange={(e) => setCta("host_subtext", e.target.value)} data-testid="share-growth-host-subtext" />
          </div>
          <div>
            <Label className="text-xs">CTA label</Label>
            <Input value={cta.host_cta_label || ""} onChange={(e) => setCta("host_cta_label", e.target.value)} placeholder="Become a host →" data-testid="share-growth-host-label" />
          </div>
        </CardContent>
      </Card>

      <div className="rounded-lg bg-purple-50 border border-purple-100 p-4 text-xs text-purple-900">
        <p className="font-semibold mb-1">Growth loop</p>
        <p>Both CTAs are tracked in <code>db.growth_loop_tracking</code>. View funnel &amp; conversion rates at <code>GET /api/admin/growth-loop-analytics</code>.</p>
      </div>
    </div>
  );
}

